import { io, Socket } from 'socket.io-client';
import { getServerUrl } from '../config';
import { fetchSamsungDevices, SamsungDevice } from './samsungApi';
import { eventBus } from './EventBus';
import { patchRoomCardLive } from '../utils/roomCardIdentity';
import { createRoomMembership } from '../utils/roomMembership';

export const mapaSamsung: Record<number, SamsungDevice> = {};
let loaded = false;
let socket: Socket | null = null;
/** Un solo GET a la vez: un refresco vacío o atrasado no pisa al que ya mostró las tarjetas. */
let refreshInFlight: Promise<SamsungDevice[]> | null = null;

export function isMapaSamsungCargado(): boolean {
  return loaded;
}

const membership = createRoomMembership<SamsungDevice>();

export function getSamsungByHabitacion(habitacionId: number): SamsungDevice[] {
  return membership.cardsInRoom(habitacionId, mapaSamsung);
}

/** La habitación que está abierta: este id sigue siendo de esa habitación. */
export function ensureSamsungInRoom(
  roomId: number,
  device: Partial<SamsungDevice> & { id: number },
): void {
  const id = Number(device.id);
  if (!membership.ensureInRoom(roomId, { ...device, id })) return;
  const locked = membership.cardsInRoom(roomId, mapaSamsung).find((d) => Number(d.id) === id);
  const prev = mapaSamsung[id];
  if (locked) {
    mapaSamsung[id] = { ...(prev || locked), ...locked, id };
  }
  eventBus.emit('samsung:updated');
}

export function unlockSamsungRoom(id: number): void {
  membership.unlock(id);
}

/** Lista completa del servidor: actualiza los que vienen, no borra los que faltan. */
function mergeList(devices: SamsungDevice[]) {
  let changed = false;
  devices.forEach((d) => {
    if (!d?.id) return;
    const id = Number(d.id);
    mapaSamsung[id] = { ...(mapaSamsung[id] || {}), ...d, id };
    membership.lockFromList(mapaSamsung[id]);
    changed = true;
  });
  if (changed) eventBus.emit('samsung:updated');
}

/**
 * Estado en vivo (socket, refresh cada 5s, control).
 * No pisa habitación, nombre ni enabled=false: eso sacaba la tarjeta de Cocina
 * y el próximo tick ya no la volvía a pedir.
 */
function mergeLive(devices: Array<Partial<SamsungDevice> & { id?: number }>) {
  let changed = false;
  devices.forEach((d) => {
    const id = Number(d?.id);
    if (!Number.isFinite(id)) return;
    const next = patchRoomCardLive(mapaSamsung[id], { ...d, id });
    if (!next) return;
    mapaSamsung[id] = next;
    changed = true;
  });
  if (changed) eventBus.emit('samsung:updated');
}

/** Actualiza un device en el mapa (p.ej. tras control) y notifica UI. */
export function upsertSamsungDevice(device: SamsungDevice): void {
  if (!device?.id) return;
  mergeLive([device]);
}

async function refrescarSamsungNow(): Promise<SamsungDevice[]> {
  try {
    const list = await fetchSamsungDevices();
    if (!Array.isArray(list)) return Object.values(mapaSamsung);
    // Lista vacía con tarjetas ya visibles: no borrar (respuesta fallida o pedido pisado).
    if (list.length === 0 && Object.keys(mapaSamsung).length > 0) {
      loaded = true;
      return Object.values(mapaSamsung);
    }
    mergeList(list);
    loaded = true;
    return Object.values(mapaSamsung);
  } catch {
    loaded = true;
    return Object.values(mapaSamsung);
  }
}

export function refrescarSamsung(): Promise<SamsungDevice[]> {
  if (refreshInFlight) return refreshInFlight;
  refreshInFlight = refrescarSamsungNow().finally(() => {
    refreshInFlight = null;
  });
  return refreshInFlight;
}

export async function cargarSamsung(): Promise<SamsungDevice[]> {
  ensureSocket();
  return refrescarSamsung();
}

function ensureSocket() {
  if (socket) return;
  const token = localStorage.getItem('token');
  if (!token) return;
  let serverUrl = getServerUrl();
  if (typeof window !== 'undefined' && window.location.protocol === 'https:') {
    serverUrl = serverUrl.replace(/^http:/, 'https:').replace(/^ws:/, 'wss:');
  }
  socket = io(serverUrl, {
    path: '/socket.io-samsung',
    auth: { token },
    transports: ['websocket', 'polling'],
    reconnection: true,
  });
  socket.on('samsung_status', (data: Partial<SamsungDevice> & { id?: number }) => {
    if (!data?.id) return;
    const id = Number(data.id);
    const existing = mapaSamsung[id];
    if (!existing) {
      void refrescarSamsung();
      return;
    }
    // Ignorar null/undefined: un poll parcial no debe borrar temperatura/setpoint.
    mergeLive([{ ...data, id }]);
  });
}
